import { useEffect, useState } from "react";
import { useNovexData } from "@/hooks/useNovexData";
import { api } from "@/lib/api";
import { ExplainButton } from "@/components/ExplainButton";
import { TrendingUp, TrendingDown, Landmark, Sparkles, Star } from "lucide-react";
import { useWatchlist } from "@/context/WatchlistContext";
import { useLang } from "@/context/LanguageContext";
import { watchedCotCodes } from "@/lib/assets";

function CotRow({ it, t, num }) {
  const lp = Math.round(it.long_pct ?? 0);
  const sp = 100 - lp;
  const up = it.net_change > 0;
  return (
    <div className="border-b border-[#222733] py-2.5 last:border-0" data-testid={`cot-${it.market}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-slate-100">{it.market}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${it.net >= 0 ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
            {it.bias === "Long" ? t("c.netLong") : it.bias === "Short" ? t("c.netShort") : t("c.neutral")}
          </span>
        </div>
        <span className={`flex items-center gap-1 text-[11px] font-medium ${up ? "text-[#10B981]" : "text-[#EF4444]"}`}>
          {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {it.net_change > 0 ? "+" : ""}{num(it.net_change)}
        </span>
      </div>
      <div className="flex h-5 w-full overflow-hidden rounded bg-[#EF4444]/30 text-[10px] font-bold">
        <div className="flex items-center justify-start bg-[#10B981] pl-1.5 text-black/70" style={{ width: `${lp}%` }}>{lp >= 12 ? `${lp}%` : ""}</div>
        <div className="flex flex-1 items-center justify-end pr-1.5 text-[#EF4444]">{sp >= 12 ? `${sp}%` : ""}</div>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-600">
        <span>{t("c.long")} {num(it.long)}</span>
        <span>{t("c.net")} {it.net > 0 ? "+" : ""}{num(it.net)}</span>
        <span>{t("c.short")} {num(it.short)}</span>
      </div>
    </div>
  );
}

export function CotPositioning({ compact = false }) {
  const { t, lang } = useLang();
  const num = (n) => Number(n || 0).toLocaleString(lang === "en" ? "en-US" : "fr-FR");
  const { status, data, error } = useNovexData("/novex/cot");
  const { watchlist } = useWatchlist();
  const [onlyWatch, setOnlyWatch] = useState(false);
  const items = data?.items || [];
  const reportDate = data?.report_date;
  const codes = watchedCotCodes(watchlist);
  const filtered = onlyWatch && codes.length ? items.filter((it) => codes.includes(it.market)) : items;
  const shown = compact ? filtered.slice(0, 6) : filtered;

  const [reading, setReading] = useState(null);
  useEffect(() => {
    if (!items.length) return;
    let alive = true;
    api.post("/ai/cot-reading", { lang }).then(({ data }) => { if (alive) setReading(data.reading); }).catch(() => {});
    return () => { alive = false; };
  }, [items.length, reportDate, lang]);

  if (status === "loading") return <div className="space-y-2">{[...Array(compact ? 4 : 8)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-slate-800/50" />)}</div>;
  if (error) return <p className="text-sm text-amber-500/80">{error}</p>;
  if (items.length === 0) return <p className="text-sm text-slate-500">{t("c.cotNone")}</p>;

  return (
    <div data-testid="cot-positioning">
      {reading && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-900/40 bg-amber-950/20 p-3" data-testid="cot-ai-reading">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <p className="text-sm leading-relaxed text-amber-100/90">{reading}</p>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-purple-300">
            <Landmark className="h-2.5 w-2.5" /> CFTC
          </span>
          {reportDate && <span className="text-[11px] text-slate-500">{t("c.cotReport", { date: reportDate })}</span>}
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-600">
          {codes.length > 0 && (
            <button onClick={() => setOnlyWatch((v) => !v)} data-testid="cot-watchlist-toggle"
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium normal-case transition-colors ${onlyWatch ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
              <Star className={`h-2.5 w-2.5 ${onlyWatch ? "fill-amber-400 text-amber-400" : ""}`} /> {t("c.watchlist")}
            </button>
          )}
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#10B981]" /> {t("c.long")}</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#EF4444]" /> {t("c.short")}</span>
          {!compact && <ExplainButton kind="positionnement COT (Commitments of Traders)" text={`COT ${reportDate}: ${items.map((i) => `${i.market} net ${i.net} (${i.bias})`).join(", ")}`} label={t("c.analyze")} testid="explain-cot" />}
        </div>
      </div>
      <div className={compact ? "max-h-[380px] overflow-y-auto pr-1" : ""}>
        {shown.length === 0 ? <p className="py-4 text-sm text-slate-500">{t("c.cotNoWatch")}</p> : shown.map((it) => <CotRow key={it.market} it={it} t={t} num={num} />)}
      </div>
    </div>
  );
}
